import { useEffect, useState } from 'react';

import {
  ALPHA_VANTAGE_API_KEY,
  CACHE_TTL_MS,
  fallbackData,
  liveDataCache,
} from '../data';
import type { LiveMarketData } from '../types';

export function useLiveMarketData(ticker: string): LiveMarketData {
  const [liveData, setLiveData] = useState<LiveMarketData>({
    price: 0,
    change: 0,
    changePercent: 0,
    previousClose: 0,
    latestTradingDay: '',
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const fetchLiveData = async (): Promise<void> => {
      if (!ticker || ticker === '???') return;

      const tickerKey = ticker.toUpperCase();
      const now = Date.now();

      const cached = liveDataCache[tickerKey];
      if (cached && now - cached.cachedAt < CACHE_TTL_MS) {
        setLiveData({
          price: cached.price,
          change: cached.change,
          changePercent: cached.changePercent,
          previousClose: cached.previousClose,
          latestTradingDay: cached.latestTradingDay,
          isLoading: false,
          error: null,
        });
        return;
      }

      setLiveData(prev => ({ ...prev, isLoading: true, error: null }));

      if (!ALPHA_VANTAGE_API_KEY) {
        const fb = fallbackData[tickerKey];
        if (fb) {
          setLiveData({ ...fb, isLoading: false, error: null });
        } else {
          setLiveData(prev => ({
            ...prev,
            isLoading: false,
            error: 'Alpha Vantage API key not configured',
          }));
        }
        return;
      }

      try {
        const response = await fetch(
          `https://www.alphavantage.co/query?function=GLOBAL_QUOTE&symbol=${tickerKey}&apikey=${ALPHA_VANTAGE_API_KEY}`
        );
        const data = await response.json();
        console.log('[Alpha Vantage] Response for', tickerKey, ':', data);

        if (
          data['Global Quote'] &&
          Object.keys(data['Global Quote']).length > 0
        ) {
          const quote = data['Global Quote'];
          const apiDate = quote['07. latest trading day'] || '';
          let formattedDate = apiDate;
          if (apiDate) {
            const d = new Date(apiDate + 'T00:00:00');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            formattedDate = `${month}-${day}-${d.getFullYear()}`;
          }
          const parsedData = {
            price: parseFloat(quote['05. price']) || 0,
            change: parseFloat(quote['09. change']) || 0,
            changePercent:
              parseFloat(quote['10. change percent']?.replace('%', '')) || 0,
            previousClose: parseFloat(quote['08. previous close']) || 0,
            latestTradingDay: formattedDate,
          };
          liveDataCache[tickerKey] = { ...parsedData, cachedAt: now };
          fallbackData[tickerKey] = parsedData;
          setLiveData({
            ...parsedData,
            isLoading: false,
            error: null,
          });
        } else if (data['Note'] || data['Information']) {
          console.log('[Alpha Vantage] Rate limit hit, using fallback');
          const fb = fallbackData[tickerKey];
          if (fb) {
            setLiveData({
              ...fb,
              isLoading: false,
              error: null,
            });
          } else {
            setLiveData(prev => ({
              ...prev,
              isLoading: false,
              error: 'API rate limit reached',
            }));
          }
        } else {
          const fb = fallbackData[tickerKey];
          if (fb) {
            setLiveData({
              ...fb,
              isLoading: false,
              error: null,
            });
          } else {
            setLiveData(prev => ({
              ...prev,
              isLoading: false,
              error: 'No data available',
            }));
          }
        }
      } catch (err) {
        console.error('[Alpha Vantage] Fetch error:', err);
        const fb = fallbackData[tickerKey];
        if (fb) {
          setLiveData({
            ...fb,
            isLoading: false,
            error: null,
          });
        } else {
          setLiveData(prev => ({
            ...prev,
            isLoading: false,
            error: 'Failed to fetch',
          }));
        }
      }
    };

    fetchLiveData();
  }, [ticker]);

  return liveData;
}
